/**
 * ProblemPreviewPanel — 학습(/students-hub) 우측 패널의 "문제 미리보기 + 테스트/리포트 탭"
 *
 * 데이터: useTests(/api/exams/summary) → 학생의 실제 테스트 이력(+ 전체 시험 allTests).
 * 우측 리스트 칸은 HubTestReportTabs([테스트 목록 | 리포트 목록] 탭)로, 공유 TestHistoryPanel을
 * 수정하지 않는 이원화 방식(StudentsHub 로컬 fork). 리포트 목록/상세는 v2 API(reportApi.js) 연동.
 *
 * 미리보기는 두 종류의 필터가 교집합으로 동작한다.
 * - 수학개념 필터(selectedConcepts, 다중): AITracker 개념 클릭으로 추가/토글.
 *   선택한 개념을 "모두 가진"(AND) 푼 문제만 표시. problem_id → concept_ids 매핑은
 *   v2 묶음 조회(problem-search?problem_ids)로 가져온다(개념-무관 맵, 마운트당 1회).
 * - 테스트 필터(selectedTest): 테스트 목록에서 선택 → 그 테스트 범위로 좁힌다.
 *   · 개념만: 전체 테스트(전체 풀이 이력)에서 AND 검색
 *   · 개념 + 테스트: 선택 테스트 안에서 AND 검색
 *   · 개념 없음: 선택 테스트의 문제(기존 동작)
 * 첫 개념을 선택하면 전체 테스트로 시작하고, 테스트 선택은 선택적 좁히기로 작동한다.
 * - 강의 행 보기(syllabusRow): 학습 화면에서 강의 행을 누르면 개념 필터 대신 그 행의 검색 조건 그대로 × 이 학생의 v2 풀이 기록
 *   (useSyllabusRowProblems, 설계 FE-20260914 §8 D10). 그 행의 매스컨셉 칩과 개념 조건 단추(하나라도·모두·정확히)를 보인다 —
 *   단추는 처음엔 강의에 저장된 방식이고, 누르면 하나라도 → 모두 → 정확히 로 바꿔 서버에 다시 묻는다(강의에는 저장하지 않음).
 *   테스트는 그대로 좁히기다.
 *
 * vertical/fillHeight 로 가로(카드|리스트) / 세로(리스트 위·카드 아래) 레이아웃을 전환한다.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef, Suspense, lazy } from 'react';
import { X, Check, RotateCcw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTests } from '../../pages/LearningStatus/hooks/useTests';
// 개념 사전·문항 개념 둘 다 v2 다. v1(/api/mathcepts/temp·…/by-prob-ids)을 여기에 다시
// 끌어오지 마라 — 그 순간 이 패널이 v2 전용이 아니게 된다(오너 방침 2026-08-26).
// 카드가 v2 로 개념을 그리는데 필터만 v1 맵으로 거르면 **거른 결과와 보이는 개념이 갈린다.**
import { fetchConceptDictV2 } from '../ExamPapersV2/conceptBadge';
import { fetchProblemItemsV2 } from '../ExamPapersV2/problemBodyV2';
import { useSyllabusRowProblems } from './useSyllabusRowProblems';
import {
  conceptMatchLabel, uniformConceptMatch, withConceptMatch, CONCEPT_MATCH_ORDER,
} from './syllabusProgress';
import { MATCH_LABEL } from '../SyllabusV2/syllabusQuery';
import katex from 'katex';
import 'katex/dist/katex.min.css';
// v2 전용 카드형 — 구형 ProblemPreviewCards 는 대상 밖 세 화면(LearningStatus 두 자리·
// ProblemAgent/WorkDetail)이 그대로 쓰고 있어 **손대지 않고 옆에 세운 것**이다.
// 받는 props 는 구형과 같아 이 자리는 이름만 바뀐다.
import ProblemCardsV2 from '../ExamPapersV2/ProblemCardsV2';
import HubTestReportTabs from './HubTestReportTabs';
// v1 테스트와 v2 배정이 이 패널의 selectedTest 한 자리를 나눠 쓴다 — 두 번호 공간을 가르는 키
import { testKey } from './assignmentResult';

const ProblemPreviewV2 = lazy(() => import('../ExamPapersV2/ProblemPreviewV2'));

// 테스트 목록을 실시(생성)일 기준 전체 시계열 내림차순으로 정렬 (최근 실시가 최상단)
const TEST_SORT_OPTS = { sortBy: 'created_desc' };

// 선택 개념 칩 = 문제 카드의 개념 표시(ProblemPreviewCards)와 동일 디자인: 순번 배지 + 컨셉타입 배지 + 개념명(LaTeX).
const getConceptTypeColor = (type) => ({
  DEF: 'rgba(239, 68, 68, 0.5)', AXM: 'rgba(249, 115, 22, 0.5)', IDN: 'rgba(245, 158, 11, 0.5)',
  THM: 'rgba(34, 197, 94, 0.5)', MTH: 'rgba(6, 182, 212, 0.5)', PRP: 'rgba(59, 130, 246, 0.5)',
  FOR: 'rgba(139, 92, 246, 0.5)', NTN: 'rgba(236, 72, 153, 0.5)',
}[type] || 'rgba(156, 163, 175, 0.5)');

const renderLatex = (text) => {
  if (!text) return '';
  try {
    const re = /\$([^$]+)\$/g;
    const parts = [];
    let last = 0;
    let m = re.exec(text);
    while (m !== null) {
      if (m.index > last) parts.push(text.substring(last, m.index));
      parts.push(`<span class="katex-inline">${katex.renderToString(m[1], { throwOnError: false, displayMode: false })}</span>`);
      last = m.index + m[0].length;
      m = re.exec(text);
    }
    if (last < text.length) parts.push(text.substring(last));
    return parts.join('');
  } catch {
    return text;
  }
};

function ProblemPreviewPanel({
  studentId, student, vertical = false, fillHeight = false, selectedConcepts = [], onRemoveConcept, onClearConcepts,
  syllabusRow = null,
  onLearningChanged,
}) {
  const { getAccessToken } = useAuth();
  const numericId = parseInt(studentId, 10);

  // useTests 는 selectedStudent 식별자 변경 시마다 리셋/재요청한다.
  // studentId 만으로 메모이즈하여 student 정보 로드로 인한 불필요한 재요청을 막는다.
  const testStudent = useMemo(() => ({ student_id: numericId }), [numericId]);

  const {
    testHistory,
    allTests,
    isTestHistoryLoading,
    hasMoreTests,
    testHistoryRef,
    loadMoreTests,
  } = useTests(testStudent, undefined, TEST_SORT_OPTS);

  const [selectedTest, setSelectedTest] = useState(null);
  const [wrongOnly, setWrongOnly] = useState(false);  // 틀린 문제만 보기 (정오답 result===false)
  const [conceptMatchAll, setConceptMatchAll] = useState(false); // true=AND(모두 포함) / false=OR(하나라도)
  // 강의 행 보기(D10) — 그 행의 검색 조건 그대로 × 이 학생의 v2 풀이 기록. 이 동안 개념 거르기는 쓰지 않는다
  const rowKey = syllabusRow?.key ?? null;
  // 개념 조건 단추 — 처음엔 강의에 저장된 방식, 누르면 하나라도 → 모두 → 정확히 로 바꿔 서버에 다시 묻는다(강의에는 저장하지 않음).
  // 고른 방식은 그 행에만 붙는다 — 다른 행을 고르면 그 행의 저장된 방식으로 돌아간다(효과로 되돌리지 않아 옛 방식으로 한 번 더 묻지 않는다)
  const [rowMatchPick, setRowMatchPick] = useState({ key: null, match: null });
  const rowMatch = rowMatchPick.key === rowKey ? rowMatchPick.match : null;
  // 행이 바뀌면 고른 방식을 지운다 — 열쇠가 달라 바뀐 첫 그림부터 저장된 방식이고, 지워 두어야 그 행으로 돌아왔을 때도 저장된 방식이다
  useEffect(() => { setRowMatchPick((p) => (p.key == null ? p : { key: null, match: null })); }, [rowKey]);
  const rowSavedLabel = useMemo(() => (syllabusRow ? conceptMatchLabel(syllabusRow.query) : ''), [syllabusRow]);
  const rowCurrentMatch = rowMatch || (syllabusRow ? uniformConceptMatch(syllabusRow.query) : null);
  const rowNextMatch = CONCEPT_MATCH_ORDER[(CONCEPT_MATCH_ORDER.indexOf(rowCurrentMatch) + 1) % CONCEPT_MATCH_ORDER.length];
  const rowMatchLabel = rowMatch ? MATCH_LABEL[rowMatch] : rowSavedLabel;
  const cycleRowMatch = useCallback(() => setRowMatchPick({ key: rowKey, match: rowNextMatch }), [rowKey, rowNextMatch]);
  const effectiveRow = useMemo(() => (
    syllabusRow && rowMatch
      ? { ...syllabusRow, key: `${syllabusRow.key}|${rowMatch}`, query: withConceptMatch(syllabusRow.query, rowMatch) }
      : syllabusRow
  ), [syllabusRow, rowMatch]);
  const rowView = useSyllabusRowProblems(numericId, effectiveRow, getAccessToken);
  const rowGetProblemResult = useCallback((_test, problemId) => rowView.resultById?.get(problemId) ?? null, [rowView]);
  // 강의 행을 새로 고르면 전체 풀이에서 찾는다 — 개념을 처음 고를 때 테스트 좁히기를 푸는 것과 같은 규칙
  useEffect(() => { if (rowKey) setSelectedTest(null); }, [rowKey]);
  const hasConcepts = selectedConcepts.length > 0;
  // 선택 개념 조합 서명 — 값이 바뀔 때만 문항 개념 맵 로드 effect를 재시도(에러 상태에서 탈출용).
  const conceptSignature = selectedConcepts.map((c) => c.cid).join(',');

  // 초기에는 테스트를 자동 선택하지 않는다(prompt 상태). 사용자가 테스트나 개념을 직접 골라야 한다.

  // 첫 개념을 선택하면(개념셋: 빈→있음) 전체 테스트에서 검색하도록 테스트 필터를 초기화한다.
  // (이후 테스트를 누르면 그 테스트로 좁혀지고, 개념 추가/제거는 현재 스코프를 유지)
  useEffect(() => {
    if (hasConcepts) setSelectedTest(null);
  }, [hasConcepts]);

  // 테스트 기준 정오답 (기존 동작)
  const getProblemResult = useCallback((test, problemId) => {
    if (!test || !test.result) return null;
    const r = test.result[problemId];
    return r === undefined ? null : r;
  }, []);

  // ── 수학개념 필터용 데이터 ───────────────────────────────────────────────
  // 풀었던 문제 집합(전체 시험, 중복 제거·최신 결과 우선). allTests 는 최신순 정렬.
  const attempted = useMemo(() => {
    const map = new Map();
    (allTests || []).forEach((t) => {
      const res = t.result || {};
      (t.problems_list || []).forEach((p) => {
        if (!map.has(p.problem_id)) {
          const r = res[p.problem_id];
          map.set(p.problem_id, {
            problem_id: p.problem_id,
            step: p.step,
            address: p.address,
            correct: r === true ? true : r === false ? false : null,
          });
        }
      });
    });
    return map;
  }, [allTests]);

  // problem_id → concept_ids 맵 (개념 클릭 시 1회 로드, 마운트당 캐시).
  // 맵은 선택 개념과 무관(모든 푼 문제의 concept_ids)하다. status: idle|loading|loaded|error.
  const [conceptMap, setConceptMap] = useState(null);
  const [conceptMapStatus, setConceptMapStatus] = useState('idle');
  const conceptFetchRef = useRef(false);

  // 개념 메타(cid → {order, concept_type, concept_name}) — 선택 칩을 문제 카드 디자인으로 표출.
  const [conceptMetaByCid, setConceptMetaByCid] = useState(null);
  // v2 사전(계약 §8)은 원본 키를 그대로 두고 id·name 만 얹어 주므로 아래 소비부
  // (meta.concept_name·order·concept_type)가 손대지 않고 그대로 돈다.
  // 모듈 캐시(5분)를 카드·창과 나눠 써 같은 사전을 세 번 받지 않는다.
  useEffect(() => {
    let alive = true;
    Promise.resolve(fetchConceptDictV2(getAccessToken))
      .then((list) => { if (alive && Array.isArray(list)) setConceptMetaByCid(new Map(list.map((m) => [String(m.id), m]))); })
      .catch(() => {});
    return () => { alive = false; };
  }, [getAccessToken]);

  useEffect(() => {
    if (!hasConcepts) return;              // 개념 선택 시에만 로드
    if (conceptMap) return;                // 이미 로드됨 (마운트당 1회)
    if (attempted.size === 0) return;      // 풀이 이력 로드 후
    if (conceptFetchRef.current) return;   // 동시 중복 호출 방지
    conceptFetchRef.current = true;
    setConceptMapStatus('loading');
    // 맵이 개념-무관하므로 진행 중 개념이 바뀌어도 완료 결과를 그대로 쓴다(취소 불필요).
    // 학생 전환 시에는 패널이 remount 되어 상태가 초기화된다.
    (async () => {
      try {
        const ids = Array.from(attempted.keys());
        // 덩이 나누기(100)·병렬·부분 실패 흡수가 이 함수 안에 이미 있다 — 여기서 다시 짜지 않는다.
        // withBody 는 켜지 않는다(기본 꺼짐): 필요한 것이 concept_ids 뿐인데 켜면 푼 문제 전부의
        // 본문이 딸려 와, 수백 문항짜리 학생에서 응답이 통째로 무거워진다.
        const items = await fetchProblemItemsV2(ids, getAccessToken);
        if (items == null) throw new Error('문항 개념을 불러오지 못했습니다.');
        const map = {};
        // 물어본 id 를 **빠짐없이** 채운다. 검색은 status=999(본문 확정) 문항만 주므로 응답에
        // 없는 id 가 생기는데, 키를 비워 두면 아래 필터의 `!pc`(아직 안 옴)와 `pc.length===0`
        // (개념이 없음)이 같은 자리에서 섞인다. 결과는 어느 쪽이든 제외로 같지만,
        // 값을 명시해 두어야 나중에 둘을 갈라 다루려 할 때 이 자리를 다시 읽지 않는다.
        ids.forEach((id) => { map[id] = items[String(id)]?.concept_ids || []; });
        setConceptMap(map);
        setConceptMapStatus('loaded');
      } catch (e) {
        console.error('개념-문제 매핑 로드 실패:', e);
        // 맵을 비우지 않음 → conceptMap 은 null 유지, 다음 개념 선택 때 재시도된다.
        setConceptMapStatus('error');
      } finally {
        conceptFetchRef.current = false;
      }
    })();
    // conceptSignature: 에러 후 개념을 바꿔 재선택하면(conceptMap=null·ref=false 상태) effect가 다시 돌아 재시도된다.
    //   로드 완료(conceptMap 존재)·로딩 중(ref=true)에는 위 가드가 no-op으로 만들어 재요청하지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConcepts, conceptMap, attempted, getAccessToken, conceptSignature]);

  // 개념 필터 모드의 정오답 (테스트와 무관하게 풀이 이력 기준)
  const conceptGetProblemResult = useCallback((_test, problemId) => {
    const info = attempted.get(problemId);
    return info ? info.correct : null;
  }, [attempted]);

  /**
   * 지금 골라진 것이 v2 시험지 배정인가. v1 테스트와 같은 자리(selectedTest)에 앉지만
   * 아래 두 곳에서 갈린다(2026-09-07).
   */
  const assignmentSelected = selectedTest?.kind === 'assignment';

  // ── 현재 표시할 문제 목록/상태 (개념 ∩ 테스트 ∩ 틀린문제) ──────────────────
  const view = useMemo(() => {
    // 틀린 문제만: 표시와 동일한 정오답 기준(테스트 선택 시 그 테스트, 아니면 풀이 이력)으로 result===false 만
    const resultOf = (p) => {
      const pid = (p && p.problem_id != null) ? p.problem_id : (Array.isArray(p) ? p[0] : null);
      return selectedTest ? getProblemResult(selectedTest, pid) : conceptGetProblemResult(null, pid);
    };
    const applyWrong = (list) => (wrongOnly ? list.filter((p) => resultOf(p) === false) : list);

    // 배정은 목록에 문항이 없어 상세를 한 번 더 묻는다 — 그 사이를 「없음」으로 그리지 않는다
    if (selectedTest?.pending) return { status: 'loading', problems: [] };

    // 강의 행 보기(D10) — 서버가 그 행의 식 그대로 거른 푼 문제. 테스트를 고르면 그 안으로 좁히고, 틀린 문제만은 v2 최근 결과로
    if (syllabusRow) {
      if (rowView.status === 'error') return { status: 'error', problems: [] };
      if (rowView.status !== 'ok') return { status: 'loading', problems: [] };
      const pidOf = (p) => ((p && p.problem_id != null) ? p.problem_id : (Array.isArray(p) ? p[0] : null));
      const rowResult = (pid) => (selectedTest ? getProblemResult(selectedTest, pid) : (rowView.resultById?.get(pid) ?? null));
      let list = rowView.problems;
      if (selectedTest) {
        const inTest = new Set((selectedTest.problems_list || selectedTest.sequence || []).map(pidOf));
        list = list.filter((p) => inTest.has(p.problem_id));
      }
      if (wrongOnly) list = list.filter((p) => rowResult(p.problem_id) === false);
      return { status: list.length ? 'list' : 'empty', problems: list };
    }

    /**
     * 개념 필터는 **v2 시험지 배정에 걸지 않는다.**
     *
     * 개념 필터가 세는 것은 `conceptMap` 인데, 그 맵은 v1 풀이 이력(attempted)의 문항 id 로만
     * 채워진다. 배정 문항은 거기 없어 전부 「개념 없음」으로 걸러지고, 화면은 「조건에 맞는 문제가
     * 없습니다」라고 말한다 — 실제로는 **셈할 수 없었을 뿐**이라 그 문장이 거짓이 된다.
     * 셀 수 없는 것을 없다고 하느니, 방금 고른 시험지의 문항을 그대로 보여 준다.
     * 개념 칩 옆에 그 사실을 적어 두어 화면이 조용히 다르게 굴지 않게 한다.
     */
    if (hasConcepts && !assignmentSelected) {
      if (conceptMapStatus === 'error') return { status: 'error', problems: [] };
      // 테스트 이력 로드가 끝났는데 푼 문제가 없으면(exams/summary 실패·빈 응답 포함) 무한 스피너 대신 empty 처리.
      if ((allTests || []).length === 0) {
        return { status: isTestHistoryLoading ? 'loading' : 'empty', problems: [] };
      }
      if (!conceptMap) {
        return { status: 'loading', problems: [] }; // 문항 개념 맵 로딩 중
      }
      // 개념 id 정규화(문자열) — engine concept_index(cid)와 v2 응답 concept_ids 의 int/str 드리프트 방어
      const needCids = selectedConcepts.map((c) => String(c.cid));
      // 테스트 범위: 선택 테스트 있으면 그 테스트, 없으면 전체 풀이 이력
      const universe = selectedTest
        ? (selectedTest.problems_list || [])
        : Array.from(attempted.values());
      // AND(모두 포함)/OR(하나라도): 선택 개념 매칭 문제만 (+ 틀린문제 후필터)
      const problems = applyWrong(universe.filter((p) => {
        const pc = conceptMap[p.problem_id];
        if (!pc || pc.length === 0) return false;
        const pcSet = new Set(pc.map(String));
        return conceptMatchAll
          ? needCids.every((cid) => pcSet.has(cid))
          : needCids.some((cid) => pcSet.has(cid));
      }));
      return { status: problems.length ? 'list' : 'empty', problems };
    }
    // 개념 없음 → 테스트 모드 (+ 틀린문제 후필터)
    if (selectedTest) {
      const problems = applyWrong(selectedTest.problems_list || selectedTest.sequence || []);
      return { status: problems.length ? 'list' : 'empty', problems };
    }
    return { status: isTestHistoryLoading ? 'loading' : 'prompt', problems: [] };
  }, [syllabusRow, rowView, hasConcepts, assignmentSelected, selectedConcepts, conceptMap, conceptMapStatus, allTests, selectedTest, attempted, isTestHistoryLoading, wrongOnly, conceptMatchAll, getProblemResult, conceptGetProblemResult]);

  // 테스트 선택은 개념 필터를 해제하지 않고 "좁히기"로 작동한다.
  // 선택된 테스트를 다시 누르면 해제 → 개념 있으면 전체 테스트, 없으면 프롬프트 상태.
  // 견주는 자는 testKey 다 — exam_id 끼리만 견주면 그 값이 없는 v2 배정끼리 서로 같아 보인다.
  const handleSelectTest = useCallback((t) => {
    setSelectedTest((prev) => {
      const k = testKey(prev);
      return k != null && k === testKey(t) ? null : t;
    });
  }, []);

  // 문제 보기 모달
  const [previewState, setPreviewState] = useState({ ids: [], correct: [null], number: [1] });
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handleOpenProblemPreview = useCallback((problemIds, index, isCorrect = null) => {
    const ids = Array.isArray(problemIds) ? problemIds : [problemIds];
    setPreviewState({
      ids,
      correct: isCorrect !== null ? [isCorrect] : [null],
      number: [index + 1],
    });
    setIsPreviewOpen(true);
  }, []);

  // 레이아웃 분기:
  // - 가로(vertical=false): 카드(좌) | 테스트리스트(우)
  // - 세로 + fillHeight: 부모 높이를 flex 로 채움 (데스크톱 2열의 한 칸)
  // - 세로 + !fillHeight: 고정 높이로 스크롤 흐름에 둠 (모바일 스택)
  const stacked = vertical;
  // 세로 모드: flex-col-reverse 로 [테스트 목록(위) / 문제 미리보기(아래)] 순서로 보이게 한다.
  const containerCls = stacked ? (fillHeight ? 'h-full flex-col-reverse min-h-0' : 'flex-col-reverse') : 'h-full flex-row min-h-0';
  const cardsCls = stacked ? (fillHeight ? 'min-h-0 flex-[3] w-full' : 'h-[400px] w-full') : 'min-h-0 flex-1';
  const listCls = stacked ? (fillHeight ? 'min-h-0 flex-[2] w-full' : 'h-[220px] w-full') : 'w-[280px] shrink-0';

  return (
    <div className={`flex w-full min-h-0 gap-3 ${containerCls}`}>
      {/* 문제 미리보기 카드 (개념 ∩ 테스트 필터) */}
      <div className={`flex min-w-[300px] flex-col overflow-hidden rounded-3xl bg-white shadow-lg ${cardsCls}`}>
        <div className="shrink-0 border-b p-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-[14px] font-semibold">
              문제 미리보기
              {view.status === 'list' && <span className="ml-1 font-normal text-gray-400">{view.problems.length}</span>}
            </h3>
            <div className="flex shrink-0 items-center gap-1.5">
              {syllabusRow && rowSavedLabel && (
                // 강의 행의 개념 조건 단추 — 처음엔 강의에 저장된 방식. 누르면 하나라도 → 모두 → 정확히 로 바꿔 서버 검색을 다시 한다
                <button
                  type="button"
                  data-row-match=""
                  onClick={cycleRowMatch}
                  title={`개념 조건: ${rowMatchLabel}${rowMatch ? ' (이 목록에서만 바꿈)' : ' (강의에 저장된 방식)'} — 클릭 시 ${MATCH_LABEL[rowNextMatch]}`}
                  className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${rowMatchLabel === '하나라도' ? 'border-amber-300 bg-amber-50 text-amber-600' : 'border-primary/40 bg-primary/10 text-primary'}`}
                >
                  {rowMatchLabel}
                </button>
              )}
              {selectedConcepts.length >= 2 && !syllabusRow && (
                <button
                  type="button"
                  onClick={() => setConceptMatchAll((v) => !v)}
                  title={conceptMatchAll ? 'AND: 선택 개념을 모두 가진 문제 (클릭 시 OR)' : 'OR: 선택 개념 중 하나라도 가진 문제 (클릭 시 AND)'}
                  className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${conceptMatchAll ? 'border-primary/40 bg-primary/10 text-primary' : 'border-amber-300 bg-amber-50 text-amber-600'}`}
                >
                  {conceptMatchAll ? '모두 포함(AND)' : '하나라도(OR)'}
                </button>
              )}
              <button
                type="button"
                onClick={() => setWrongOnly((v) => !v)}
                aria-pressed={wrongOnly}
                title="틀린 문제만 보기"
                className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                  wrongOnly ? 'border-danger/40 bg-danger/10 text-danger' : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {wrongOnly && <Check className="h-3 w-3" />}
                틀린 문제만
              </button>
              {(hasConcepts || syllabusRow) && (
                // 강의 행 보기에서도 둔다 — 개념 칩이 없는 행(라벨만 등)도 여기서 푼다
                <button
                  type="button"
                  onClick={onClearConcepts}
                  title={syllabusRow ? '강의 행 조건 해제' : '개념 필터 모두 해제'}
                  className="inline-flex shrink-0 items-center justify-center rounded-full border border-gray-300 p-1 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
                >
                  <RotateCcw className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          {hasConcepts && (
            // 개념이 많으면(9개+) 3행 그리드로 세로 채움 + 가로 스크롤(높이 캡→미리보기 안 가림),
            // 적으면 기존 좌→우 줄바꿈 유지.
            <div
              className={`mt-2 gap-1.5 ${
                selectedConcepts.length > 8
                  ? 'grid grid-flow-col grid-rows-3 auto-cols-max items-center justify-items-start overflow-x-auto pb-1'
                  : 'flex flex-wrap items-center'
              }`}
            >
              {selectedConcepts.map((c) => {
                const meta = conceptMetaByCid ? conceptMetaByCid.get(String(c.cid)) : null;
                const nameHtml = meta && meta.concept_name ? renderLatex(meta.concept_name) : (c.label || '개념');
                return (
                <span key={c.cid} className="inline-flex items-center gap-1 rounded-3xl border border-gray-200 px-2 py-0.5 text-xs text-gray-700">
                  {meta && meta.order != null && (
                    <span
                      className="inline-flex items-center justify-center font-semibold text-gray-700"
                      style={{ backgroundColor: 'rgba(156, 163, 175, 0.3)', borderRadius: '6px', width: '24px', height: '16px', minWidth: '24px', fontSize: '9px' }}
                    >
                      {meta.order}
                    </span>
                  )}
                  {meta && meta.concept_type && (
                    <span
                      className="inline-flex items-center justify-center font-semibold text-gray-800"
                      style={{ backgroundColor: getConceptTypeColor(meta.concept_type), borderRadius: '6px', width: '24px', height: '16px', minWidth: '24px', fontSize: '8px' }}
                    >
                      {meta.concept_type}
                    </span>
                  )}
                  <span dangerouslySetInnerHTML={{ __html: nameHtml }} />
                  <button type="button" onClick={() => onRemoveConcept(c.cid)} title="개념 제거" className="ml-0.5 hover:opacity-70">
                    <X className="h-3 w-3" />
                  </button>
                </span>
                );
              })}
              {selectedTest && (
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
                  테스트: {selectedTest.tree_name || `TID ${selectedTest.exam_id}`}
                  <button type="button" onClick={() => setSelectedTest(null)} title="전체 테스트로 보기" className="hover:opacity-70">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {/* 개념 필터가 이 시험지에는 걸리지 않는다는 사실을 화면이 먼저 말한다 —
                  말하지 않으면 개념을 골라 둔 채로 문항이 다 보이는 것이 고장으로 읽힌다 */}
              {assignmentSelected && !syllabusRow && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700"
                  title="이 시험지 문항의 개념 정보를 아직 받지 않아 교집합을 셀 수 없습니다 — 시험지의 문항을 그대로 보여 줍니다"
                >
                  개념 필터 미적용
                </span>
              )}
            </div>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          {view.status === 'loading' ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
              {syllabusRow
                ? '강의 행 조건으로 푼 문제를 찾는 중...'
                : (hasConcepts
                  ? ((allTests || []).length === 0 ? '풀이 이력 불러오는 중...' : '관련 문제 찾는 중...')
                  : '테스트 불러오는 중...')}
            </div>
          ) : view.status === 'prompt' ? (
            <div className="flex h-full items-center justify-center px-3 text-center text-sm text-gray-400">
              테스트 또는 수학개념 선택
            </div>
          ) : view.status === 'error' ? (
            <div className="flex h-full items-center justify-center px-3 text-center text-sm text-gray-400">
              {syllabusRow ? (rowView.error || '강의 행 조건으로 찾지 못했습니다.') : '개념 정보를 불러오지 못했습니다. 개념을 다시 선택해 주세요.'}
            </div>
          ) : view.status === 'empty' ? (
            <div className="flex h-full items-center justify-center px-3 text-center text-sm text-gray-400">
              {wrongOnly
                ? '조건에 맞는 틀린 문제가 없습니다.'
                : (selectedTest
                    ? '이 테스트에는 조건에 맞는 문제가 없습니다.'
                    : (syllabusRow
                        ? '이 강의 행의 조건에 맞는 푼 문제가 없습니다.'
                        : (selectedConcepts.length > 1
                            ? (conceptMatchAll ? '선택한 개념을 모두 가진 푼 문제가 없습니다.' : '선택한 개념 중 하나라도 가진 푼 문제가 없습니다.')
                            : '이 개념과 관련된 푼 문제가 없습니다.')))}
            </div>
          ) : (
            <ProblemCardsV2
              key={`${effectiveRow ? `row-${effectiveRow.key}` : (selectedConcepts.map((c) => c.cid).join('_') || 'noc')}-${testKey(selectedTest) ?? 'all'}-${wrongOnly ? 'w' : 'a'}-${conceptMatchAll ? 'and' : 'or'}`}
              problems={view.problems}
              selectedTest={selectedTest}
              onViewProblem={handleOpenProblemPreview}
              highlightedIndex={null}
              onHighlight={() => {}}
              getProblemResult={selectedTest ? getProblemResult : (syllabusRow ? rowGetProblemResult : conceptGetProblemResult)}
              isDesktop={true}
              showConcepts
            />
          )}
        </div>
      </div>

      {/* 테스트 목록 / 리포트 목록 (탭) */}
      <div className={`flex min-h-0 flex-col ${listCls}`}>
        <HubTestReportTabs
          testHistory={testHistory}
          selectedTest={selectedTest}
          isTestHistoryLoading={isTestHistoryLoading}
          hasMoreTests={hasMoreTests}
          onTestSelect={handleSelectTest}
          /* 배정은 「먼저 앉히고 → 도착하면 덮기」라 토글 통로를 쓸 수 없다(HubTestReportTabs 주석) */
          onTestSetDirect={setSelectedTest}
          testHistoryRef={testHistoryRef}
          onLoadMore={loadMoreTests}
          selectedStudent={student || testStudent}
          studentId={numericId}
          onLearningChanged={onLearningChanged}
          compact
        />
      </div>

      {/* 문제 보기 모달 */}
      {isPreviewOpen && (
        <Suspense fallback={null}>
          {/* problemNumbers → numbers 로 이름이 바뀌었다(신형 시그니처).
              correctList 는 그대로 — 번호의 정오답 색(맞음 초록·틀림 빨강)이 이 자리의 요점이고,
              그것 없이 옮기면 학생이 무엇을 틀렸는지가 화면에서 사라진다. */}
          <ProblemPreviewV2
            isOpen={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            problemIds={previewState.ids}
            showNumbers={true}
            correctList={previewState.correct}
            numbers={previewState.number}
          />
        </Suspense>
      )}
    </div>
  );
}

export default ProblemPreviewPanel;
